import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  ShoppingCart,
  ChefHat,
  UtensilsCrossed,
  Grid3X3,
  Package,
  BarChart3,
  Users,
  Settings as SettingsIcon,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  Store,
  ChevronDown,
  Receipt,
  Clock,
  Ban,
  HelpCircle,
  UserCircle,
  Printer,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { ShiftIndicator } from './ShiftManagement';
import { useQZPrint } from '../contexts/QZPrintContext';

// Navigation items with translation keys
const getNavigation = (t) => [
  { name: t('nav.dashboard'), href: '/dashboard', icon: LayoutDashboard, roles: ['OWNER', 'ADMIN', 'MANAGER'] },
  { name: t('nav.pos'), href: '/pos', icon: ShoppingCart, roles: ['OWNER', 'ADMIN', 'MANAGER', 'CASHIER'] },
  { name: t('nav.orders'), href: '/orders', icon: Receipt, roles: ['OWNER', 'ADMIN', 'MANAGER', 'CASHIER'] },
  { name: t('nav.kitchen_display'), href: '/kitchen', icon: ChefHat, roles: ['OWNER', 'ADMIN', 'MANAGER', 'KITCHEN'] },
  { name: t('nav.tables'), href: '/tables', icon: Grid3X3, roles: ['OWNER', 'ADMIN', 'MANAGER', 'CASHIER'] },
  { name: t('nav.customers'), href: '/customers', icon: UserCircle, roles: ['OWNER', 'ADMIN', 'MANAGER', 'CASHIER'] },
  { name: t('nav.menu'), href: '/menu', icon: UtensilsCrossed, roles: ['OWNER', 'ADMIN', 'MANAGER'] },
  { name: t('nav.inventory'), href: '/inventory', icon: Package, roles: ['OWNER', 'ADMIN', 'MANAGER'] },
  { name: t('nav.reports'), href: '/reports', icon: BarChart3, roles: ['OWNER', 'ADMIN', 'MANAGER'] },
  { name: t('nav.shifts'), href: '/shifts', icon: Clock, roles: ['OWNER', 'ADMIN', 'MANAGER'] },
  { name: t('nav.printers') || 'Printer', href: '/printers', icon: Printer, roles: ['OWNER', 'ADMIN', 'MANAGER', 'CASHIER'] },
  { name: t('nav.void_audit'), href: '/void-logs', icon: Ban, roles: ['OWNER', 'ADMIN', 'MANAGER'] },
  { name: t('nav.users'), href: '/users', icon: Users, roles: ['OWNER', 'ADMIN', 'MANAGER'] },
  { name: t('nav.settings'), href: '/settings', icon: SettingsIcon, roles: ['OWNER', 'ADMIN'] },
  { name: t('nav.help'), href: '/help', icon: HelpCircle, roles: ['OWNER', 'ADMIN', 'MANAGER', 'CASHIER', 'KITCHEN'] },
];

export const Layout = ({ children }) => {
  const { t } = useTranslation();
  const { user, logout, updateTheme, api } = useAuth();
  const { outlets, currentOutlet, switchOutlet } = useOutlet();
  const { isConnected: qzConnected, canPrintReceipt } = useQZPrint();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const [activeOrdersCount, setActiveOrdersCount] = useState(0);

  // Fetch active orders count for badge
  useEffect(() => {
    const fetchOrdersCount = async () => {
      if (!currentOutlet?.id || !api) return;
      try {
        const resp = await api.get('/orders', {
          params: { outlet_id: currentOutlet.id },
        });
        const activeCount = resp.data.filter(order => 
          ['CONFIRMED', 'PREPARING', 'READY', 'SERVED'].includes(order.status) &&
          order.payment_mode === 'POSTPAID'
        ).length;
        setActiveOrdersCount(activeCount);
      } catch (error) {
        console.error('Failed to fetch orders count:', error);
      }
    };
    
    fetchOrdersCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchOrdersCount, 30000);
    return () => clearInterval(interval);
  }, [currentOutlet?.id, api]);

  useEffect(() => {
    const theme = user?.theme_preference || 'dark';
    setIsDark(theme === 'dark');
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [user?.theme_preference]);

  const toggleTheme = async () => {
    const newTheme = isDark ? 'light' : 'dark';
    setIsDark(!isDark);
    document.documentElement.classList.toggle('dark', !isDark);
    try {
      await updateTheme(newTheme);
    } catch (error) {
      console.error('Failed to update theme:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = getNavigation(t);
  const filteredNav = navigation.filter((item) => 
    item.roles.includes(user?.role)
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 transform bg-card border-r transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4 border-b">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary">
                <UtensilsCrossed className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="text-lg font-bold text-foreground">Menu Resto</span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Outlet Selector */}
          {outlets.length > 0 && (
            <div className="px-3 py-3 border-b">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between" data-testid="outlet-selector">
                    <span className="flex items-center gap-2 truncate">
                      <Store className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{currentOutlet?.name || 'Select Outlet'}</span>
                    </span>
                    <ChevronDown className="h-4 w-4 flex-shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {outlets.map((outlet) => (
                    <DropdownMenuItem
                      key={outlet.id}
                      onClick={() => switchOutlet(outlet)}
                      className={outlet.id === currentOutlet?.id ? 'bg-accent' : ''}
                    >
                      {outlet.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Shift Indicator (for cashiers and managers) */}
          {['CASHIER', 'MANAGER', 'ADMIN', 'OWNER'].includes(user?.role) && (
            <div className="px-3 py-3 border-b">
              <ShiftIndicator />
            </div>
          )}

          {/* Printer Status Indicator - Clickable to /printers */}
          {['CASHIER', 'MANAGER', 'ADMIN', 'OWNER'].includes(user?.role) && (
            <Link to="/printers" className="block px-3 py-2 border-b hover:bg-accent transition-colors">
              <div 
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
                  qzConnected && canPrintReceipt 
                    ? 'bg-green-500/10 text-green-600' 
                    : qzConnected 
                      ? 'bg-yellow-500/10 text-yellow-600'
                      : 'bg-red-500/10 text-red-600'
                }`}
                data-testid="global-printer-status"
              >
                {qzConnected ? (
                  <Wifi className="h-4 w-4" />
                ) : (
                  <WifiOff className="h-4 w-4" />
                )}
                <span className="font-medium">
                  {qzConnected && canPrintReceipt 
                    ? 'Printer Ready' 
                    : qzConnected 
                      ? 'Setup Required'
                      : 'Printer Offline'
                  }
                </span>
              </div>
            </Link>
          )}

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-3">
              {filteredNav.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="flex-1">{item.name}</span>
                    {item.name === 'Orders' && activeOrdersCount > 0 && (
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-medium px-1.5">
                        {activeOrdersCount > 99 ? '99+' : activeOrdersCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className="border-t p-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start gap-3" data-testid="user-menu">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.role}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle">
                  {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                  {isDark ? 'Light Mode' : 'Dark Mode'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="flex h-16 items-center gap-4 border-b bg-card px-4 lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-6 w-6" />
          </Button>
          <span className="text-lg font-bold">Menu Resto</span>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto custom-scrollbar">
          {children}
        </main>
      </div>
    </div>
  );
};
